import type { AdjustmentMode, SpecType } from '@local/lib';

import { getAllowedFormulaTypes, ProductPriceBasisType, QualityFormulaType, TierMode } from '@local/lib';
import { Badge, Button, Card, Combobox, Icon, Radio, RadioButtonGroup, Tooltip } from '@salesforce/design-system-react';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type {
  ProductPriceBasisState,
  RangeFormulaRow,
  SingleValueFormulaRow,
  SpecState,
  TemplateSpecConfig,
  ThresholdFormulaRow,
} from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import { pluralize } from '../pluralize';
import {
  CONTRACT_PRICE_COMPONENTS,
  FIELD_HELP,
  FORMULA_TYPES,
  getSpecEffectCopy,
  isMultiRowFormulaType,
  SINGLE_ROW_CONFIG_BY_FORMULA_TYPE,
  TIER_MODES,
} from '../qualityFormulaMetadata';
import {
  getRangeBoundaryIssues,
  getRangeGapInfo,
  getSpecValidationIssues,
  validateContractedSpec,
} from '../qualityFormulaValidation';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { FieldHelpTooltip } from './FieldHelpTooltip';
import { FieldLabelRow } from './FieldLabelRow';
import { FormulaField } from './FormulaField';
import { RangeRowsTable } from './FormulaRowFields';
import {
  ContractPriceBasisInfo,
  DeemedPriceField,
  ProductPriceBasisDetails,
  ProductPriceBasisField,
} from './ProductPriceBasisSection';
import { SpecSectionPanel } from './SpecSectionPanel';
import { ThresholdAppliesToControl, ThresholdRowFields } from './ThresholdRowFields';
import { formatCurrency } from './utils/resultFormatting';

type SingleRowConfig = (typeof SINGLE_ROW_CONFIG_BY_FORMULA_TYPE)[keyof typeof SINGLE_ROW_CONFIG_BY_FORMULA_TYPE];

// Row/column flex primitives used throughout this file's own layout — plain inline styles (not an
// SLDS grid/gutters class pairing, which assumes `.slds-col` children) so an arbitrary gap always
// applies regardless of whether the children happen to be `.slds-col`s.
const rowFlex = (gap = 'var(--slds-g-spacing-3)'): React.CSSProperties => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap,
});
const colFlex = (gap = 'var(--slds-g-spacing-2)'): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap,
});

// Kept as standalone functions (rather than inlined in QualitySpecCard) purely so their own
// ternaries/optional-chaining don't add to that component's cyclomatic complexity.
const getTierCountLabel = (tierMode: TierMode, rowCount: number): string =>
  tierMode === TierMode.Threshold ? pluralize(rowCount, 'side') : pluralize(rowCount, 'range');

const getActiveFormulaLabel = (formulaType: QualityFormulaType, fallbackLabel: string): string =>
  FORMULA_TYPES.find(option => option.value === formulaType)?.label ?? fallbackLabel;

// The `focused` variant is never collapsible, so its content is always expanded regardless of
// `collapsible`/`state.collapsed` — both booleans are derived together (rather than inline in
// QualitySpecCardComponent) purely to keep these short boolean combinations from adding to that
// component's own complexity.
interface CollapseBehavior {
  isCollapsedSummary: boolean;
  isCollapsible: boolean;
}

const getCollapseBehavior = (
  variant: 'card' | 'focused',
  collapsible: boolean,
  collapsed: boolean,
): CollapseBehavior => {
  const isCollapsible = variant !== 'focused' && collapsible;
  return { isCollapsedSummary: isCollapsible && collapsed, isCollapsible };
};

interface FocusedHeaderDetails {
  isConfigured?: boolean;
  tierModeLabel?: string;
}

// Only the `focused` variant needs a tier-mode title suffix or a "Configured" status — extracted
// so computing both doesn't add to QualitySpecCardComponent's own cyclomatic complexity.
const getFocusedHeaderDetails = (
  isFocused: boolean,
  hasTieredRows: boolean,
  specType: SpecType,
  state: SpecState,
): FocusedHeaderDetails => {
  if (!isFocused) return {};
  return {
    isConfigured: getSpecValidationIssues(specType, state).length === 0,
    tierModeLabel: hasTieredRows ? TIER_MODES.find(mode => mode.value === state.tierMode)?.label : undefined,
  };
};

// Supplied by the page (e.g. QualityFormulasPage), not imported here, so this quality-formulas
// feature component never has to depend on the Templates feature — `onApply` lets the rendered
// control (Templates' own LoadTemplateControl) hand back a loaded config without needing to know
// this card's store exists.
type RenderLoadTemplateControl = (
  specType: SpecType,
  specLabel: string,
  onApply: (config: TemplateSpecConfig) => void,
) => React.ReactNode;

interface SpecCardHeaderProps {
  activeFormulaLabel: string;
  /** Set only when the spec's Price Basis is Deemed Price — see showsDeemedPriceField in
   * QualitySpecCard. Ignored entirely in the `focused` variant, whose subtitle already states the
   * active Formula Type. */
  deemedPriceBadgeLabel?: string;
  effectCopy: { aboveMeaning: string; belowMeaning: string };
  hasTieredRows: boolean;
  hint: string;
  isCollapsedSummary: boolean;
  /** Set to `false` by the Template Editor, where there's only ever one card and it must stay
   * expanded — hides the collapse affordance entirely rather than just disabling the click. Also
   * always `false` in the `focused` variant — see QualitySpecCard's own doc comment. */
  isCollapsible: boolean;
  /** Shows a green "Configured" badge next to Load Template, and appends the active Formula Type
   * to the subtitle — only `true` for the `focused` variant (Contract Entry's single-editor
   * workbench), where every rendered spec is already enabled, so the badge's only job is to say
   * whether it would also pass a preview run (see `getSpecValidationIssues`). */
  isConfigured?: boolean;
  isFocused: boolean;
  onApplyTemplate: (config: TemplateSpecConfig) => void;
  /** Removes this spec entirely (same `removeSpec` action the expanded navigator's own Remove
   * button uses) — only passed in the `focused` variant, where this header's Configured badge is
   * otherwise the only indicator a spec is enabled at all, with no other on-screen way to remove
   * it once the navigator is compacted (see SpecNavigator's own compact-mode doc comment). */
  onRemoveSpec?: () => void;
  onToggleCollapse: (specType: SpecType) => void;
  /** One independent badge per selected component (name + price), set only when the spec's Price
   * Basis is Price Components — see showsPriceComponentsPanel in QualitySpecCard. Ignored in the
   * `focused` variant for the same reason as `deemedPriceBadgeLabel`. */
  priceComponentBadgeLabels?: string[];
  renderLoadTemplateControl?: RenderLoadTemplateControl;
  specLabel: string;
  specType: SpecType;
  tierCountLabel: string;
  /** Appended to the title as "{specLabel} · {tierModeLabel}" — only passed in the `focused`
   * variant, and only for a tiered formula type (see QualitySpecCard's own `hasTieredRows`). */
  tierModeLabel?: string;
}

// The `focused` variant's "Configured"/"Needs attention" status pill — see `isConfigured`'s own
// doc comment on SpecCardHeaderProps for what it means.
const SpecCardConfiguredBadge: React.FC<{ isConfigured?: boolean }> = ({ isConfigured }) => (
  <Badge
    color={isConfigured ? 'success' : 'warning'}
    content={isConfigured ? 'Configured' : 'Needs attention'}
    icon={
      isConfigured ? (
        <Icon category={IconCategory.Utility} containerClassName="slds-m-right_xx-small" name="check" size="xx-small" />
      ) : undefined
    }
  />
);

// The `focused` variant's remove action — see `onRemoveSpec`'s own doc comment on
// SpecCardHeaderProps. `slds-button_icon-error` matches ConfiguredRow's own remove button in
// SpecNavigator (the library's own hook for a destructive icon button, already red in every
// state including hover/focus) so removing a spec reads the same way from either place.
const SpecCardRemoveButton: React.FC<{ onRemoveSpec: () => void; specLabel: string }> = ({
  onRemoveSpec,
  specLabel,
}) => (
  <Tooltip align="top" content={`Remove ${specLabel}`}>
    <Button
      assistiveText={{ icon: `Remove ${specLabel}` }}
      className="slds-button_icon-error"
      iconCategory={IconCategory.Utility}
      iconName="delete"
      onClick={onRemoveSpec}
      variant="icon"
    />
  </Tooltip>
);

// Shown only while `isCollapsedSummary` (a Template Editor-style stacked card, collapsed) — the
// formula type, tier count, and any Price Components/Deemed Price badges that would otherwise be
// hidden along with the rest of the card's content.
const SpecCardCollapsedSummary: React.FC<{
  activeFormulaLabel: string;
  deemedPriceBadgeLabel?: string;
  hasTieredRows: boolean;
  priceComponentBadgeLabels?: string[];
  tierCountLabel: string;
}> = ({ activeFormulaLabel, deemedPriceBadgeLabel, hasTieredRows, priceComponentBadgeLabels, tierCountLabel }) => (
  <div className="slds-m-top_xx-small" style={rowFlex('var(--slds-g-spacing-2)')}>
    <span className="slds-text-body_small slds-text-color_weak">{activeFormulaLabel}</span>
    {hasTieredRows ? <Badge color="light" content={tierCountLabel} /> : null}
    {priceComponentBadgeLabels?.map(label => (
      <Badge color="light" content={label} key={label} />
    ))}
    {deemedPriceBadgeLabel ? <Badge color="light" content={deemedPriceBadgeLabel} /> : null}
  </div>
);

// The icon + title (+ subtitle/collapsed-summary) block, plus the header-right actions (Load
// Template, Configured badge, collapse chevron) — the outer per-spec `Card`'s own header,
// composed manually (not via `SpecSectionPanel`, whose header is simpler) since this one needs a
// two-line subtitle and several conditional badges.
const SpecCardHeader: React.FC<SpecCardHeaderProps> = ({
  activeFormulaLabel,
  deemedPriceBadgeLabel,
  effectCopy,
  hasTieredRows,
  hint,
  isCollapsedSummary,
  isCollapsible,
  isConfigured,
  isFocused,
  onApplyTemplate,
  onRemoveSpec,
  onToggleCollapse,
  priceComponentBadgeLabels,
  renderLoadTemplateControl,
  specLabel,
  specType,
  tierCountLabel,
  tierModeLabel,
}) => (
  <div
    className={`qfw-card-header${isFocused ? ' qfw-card-header_focused' : ''}`}
    style={{ gap: 'var(--slds-g-spacing-3)', justifyContent: 'space-between' }}
  >
    <div style={{ ...rowFlex(), alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
      <span
        className="slds-icon_container slds-icon_container_circle slds-icon-standard-formula"
        style={{ flexShrink: 0 }}
      >
        {/* The installed `Icon`'s `category="utility"` always applies its own
            default-colorVariant class (`slds-icon-text-default`, muted gray) directly on the
            SVG — set directly on that element, it wins over the inherited white this standard
            icon container otherwise provides for a colored circle avatar (an attribute-selector
            rule keyed on this container's own class list), so it has to be overridden explicitly
            here rather than left to inherit. */}
        <Icon
          category={IconCategory.Utility}
          name="formula"
          size="small"
          style={{ fill: 'var(--slds-g-color-on-accent-1)' }}
        />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={rowFlex('var(--slds-g-spacing-1)')}>
          <h2
            className="slds-text-heading_small slds-text-color_default slds-truncate"
            style={{ fontWeight: 'var(--slds-g-font-weight-7)', margin: 0 }}
          >
            {tierModeLabel ? `${specLabel} · ${tierModeLabel}` : specLabel}
          </h2>
          <FieldHelpTooltip
            label={
              <>
                Above contracted value = <strong>{effectCopy.aboveMeaning}</strong> &nbsp;|&nbsp; Below contracted value
                = <strong>{effectCopy.belowMeaning}</strong>
              </>
            }
          />
        </div>
        <p className="slds-text-body_small slds-text-color_weak slds-truncate" style={{ margin: 0 }}>
          {isFocused ? `${hint} · Formula: ${activeFormulaLabel}` : hint}
        </p>
        {isCollapsedSummary ? (
          <SpecCardCollapsedSummary
            activeFormulaLabel={activeFormulaLabel}
            deemedPriceBadgeLabel={deemedPriceBadgeLabel}
            hasTieredRows={hasTieredRows}
            priceComponentBadgeLabels={priceComponentBadgeLabels}
            tierCountLabel={tierCountLabel}
          />
        ) : null}
      </div>
    </div>
    <div
      style={{
        ...rowFlex('var(--slds-g-spacing-2)'),
        display: isCollapsedSummary ? 'none' : 'flex',
        flexShrink: isFocused ? 1 : 0,
        minWidth: 0,
      }}
    >
      {renderLoadTemplateControl ? renderLoadTemplateControl(specType, specLabel, onApplyTemplate) : null}
      {isFocused ? <SpecCardConfiguredBadge isConfigured={isConfigured} /> : null}
      {isFocused && onRemoveSpec ? (
        <div className="qfw-card-header-remove">
          <SpecCardRemoveButton onRemoveSpec={onRemoveSpec} specLabel={specLabel} />
        </div>
      ) : null}
      {isCollapsible ? (
        <Button
          assistiveText={{ icon: isCollapsedSummary ? `Expand ${specLabel}` : `Collapse ${specLabel}` }}
          iconCategory={IconCategory.Utility}
          iconName={isCollapsedSummary ? 'chevrondown' : 'chevronup'}
          onClick={() => {
            onToggleCollapse(specType);
          }}
          variant="icon"
        />
      ) : null}
    </div>
  </div>
);

interface SpecCardRangeRowsProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

// Extracted so the range list + gap callouts don't add to the parent component's complexity. The
// "+ Add Range" trigger itself is rendered by the caller as the enclosing SpecSectionPanel's
// header action (see QualitySpecCard), not here, so it sits in the same place every panel's
// header action does.
const SpecCardRangeRows: React.FC<SpecCardRangeRowsProps> = ({
  formulaType,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => {
  const gapInfo = getRangeGapInfo(rows);
  const boundaryIssues = getRangeBoundaryIssues(rows);
  const overlapMessages = boundaryIssues.filter(issue => issue.type === 'overlap').map(issue => issue.message);
  const internalGapMessages = boundaryIssues.filter(issue => issue.type === 'gap').map(issue => issue.message);

  return (
    <div style={colFlex()}>
      {overlapMessages.length > 0 ? (
        <div className="slds-notify slds-notify_alert slds-theme_alert-texture slds-theme_error" role="alert">
          {overlapMessages.map(message => (
            <p key={message}>{message}</p>
          ))}
        </div>
      ) : null}
      {(gapInfo.belowFirstRange ?? gapInfo.aboveLastRange) !== null || internalGapMessages.length > 0 ? (
        <div className="slds-notify slds-notify_alert slds-theme_alert-texture slds-theme_warning" role="alert">
          {gapInfo.belowFirstRange ? <p>{gapInfo.belowFirstRange}</p> : null}
          {internalGapMessages.map(message => (
            <p key={message}>{message}</p>
          ))}
          {gapInfo.aboveLastRange ? <p>{gapInfo.aboveLastRange}</p> : null}
        </div>
      ) : null}
      <RangeRowsTable
        formulaType={formulaType}
        onRemoveRow={onRemoveRow}
        onRowChange={onRowChange}
        rows={rows}
        specType={specType}
      />
      {/* Scoped to internal boundaries only (not the always-present below-first/above-last edge
          notes above, which are informational, not a problem to fix) — confirms the chain of
          ranges itself is internally consistent. */}
      {overlapMessages.length === 0 && internalGapMessages.length === 0 ? (
        <div style={rowFlex('var(--slds-g-spacing-2)')}>
          <Icon category={IconCategory.Utility} colorVariant="success" name="check" size="xx-small" />
          <span className="slds-text-body_small slds-text-color_success">No internal gaps or overlapping ranges</span>
        </div>
      ) : null}
    </div>
  );
};

interface AdjustmentBehaviorPanelProps {
  contractedSpec: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof ThresholdFormulaRow,
    value: boolean | number | string,
  ) => void;
  onThresholdSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  rows: ThresholdFormulaRow[];
  /** Side-by-side rows only when the panel has the full card width. */
  sideBySide?: boolean;
  specType: SpecType;
}

const AdjustmentBehaviorPanel: React.FC<AdjustmentBehaviorPanelProps> = ({
  contractedSpec,
  formulaType,
  onRowChange,
  onThresholdSideToggle,
  rows,
  sideBySide,
  specType,
}) => (
  <SpecSectionPanel
    action={<ThresholdAppliesToControl onSideToggle={onThresholdSideToggle} rows={rows} specType={specType} />}
    className="qfw-adjustment-behavior-panel"
    description="Set the rate each side applies once it matches."
    iconName="rules"
    title="Adjustment Behavior"
  >
    <ThresholdRowFields
      contractedSpec={contractedSpec}
      formulaType={formulaType}
      onRowChange={onRowChange}
      rows={rows}
      sideBySide={sideBySide}
      specType={specType}
    />
  </SpecSectionPanel>
);

interface PriceAdjustmentRangesPanelProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onAddRow: (specType: SpecType) => void;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

// RANGE tier mode's full-width ranges table — the table's own columns already need the full card
// width, so (unlike Threshold's Applies to/Adjustment Behavior) this never shares a row.
const PriceAdjustmentRangesPanel: React.FC<PriceAdjustmentRangesPanelProps> = ({
  formulaType,
  onAddRow,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => (
  <SpecSectionPanel
    action={
      <Button
        iconCategory={IconCategory.Utility}
        iconName="add"
        iconPosition="left"
        label="Add Range"
        onClick={() => {
          onAddRow(specType);
        }}
        variant="outline-brand"
      />
    }
    description="Define the adjustment for each value range."
    iconName="rules"
    title="Price Adjustment Ranges"
  >
    <SpecCardRangeRows
      formulaType={formulaType}
      onRemoveRow={onRemoveRow}
      onRowChange={onRowChange}
      rows={rows}
      specType={specType}
    />
  </SpecSectionPanel>
);

interface SpecCardStackedLayoutProps {
  configurationPanel: React.ReactNode;
  contractedValuePanel: React.ReactNode;
  formulaType: QualityFormulaType;
  isThreshold: boolean;
  onAddRow: (specType: SpecType) => void;
  onPriceBasisChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow | keyof ThresholdFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  onThresholdSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  priceBasis: ProductPriceBasisState;
  rows: Array<RangeFormulaRow | ThresholdFormulaRow>;
  showsPriceComponentsPanel: boolean;
  specType: SpecType;
  state: SpecState;
}

// Every Price Basis except Price Components + Threshold (see showsPriceComponentsSplitLayout in
// QualitySpecCard) stacks its panels full-width. Price Components and Price Adjustment Ranges
// are individually collapsible
// (see SpecSectionPanel's own doc comment) rather than rearranged into their own row when both are
// shown (Range + Price Components) — that kept squeezing both tables narrower than they need.
// Extracted so that branching doesn't add to the parent component's own complexity. Re-derives
// `hasTieredRows` from `formulaType` itself (rather than taking it as a prop) so TypeScript's
// aliased-condition narrowing still applies to the panels below — that narrowing doesn't carry
// over a boolean computed in a different component.
const SpecCardStackedLayout: React.FC<SpecCardStackedLayoutProps> = ({
  configurationPanel,
  contractedValuePanel,
  formulaType,
  isThreshold,
  onAddRow,
  onPriceBasisChange,
  onRemoveRow,
  onRowChange,
  onThresholdSideToggle,
  priceBasis,
  rows,
  showsPriceComponentsPanel,
  specType,
  state,
}) => {
  const hasTieredRows = isMultiRowFormulaType(formulaType);

  if (hasTieredRows && isThreshold) {
    return (
      <div style={colFlex()}>
        {configurationPanel}
        {contractedValuePanel}
        <AdjustmentBehaviorPanel
          contractedSpec={state.contractedSpec}
          formulaType={formulaType}
          onRowChange={onRowChange}
          onThresholdSideToggle={onThresholdSideToggle}
          rows={rows}
          sideBySide
          specType={specType}
        />
      </div>
    );
  }

  return (
    <div style={colFlex()}>
      {configurationPanel}
      {/* Range's Price Components has no split layout to live in (see
          showsPriceComponentsSplitLayout in QualitySpecCard, which only covers Threshold), so it
          sits right after Configuration instead of trailing after Price Adjustment Ranges. Only
          here (never Threshold's split layout) is it collapsible, since it already gets its own
          column there — see ProductPriceBasisDetails' own doc comment. */}
      {showsPriceComponentsPanel ? (
        <ProductPriceBasisDetails
          collapsible
          onChange={onPriceBasisChange}
          priceBasis={priceBasis}
          specType={specType}
        />
      ) : null}
      {contractedValuePanel}
      {hasTieredRows ? (
        <PriceAdjustmentRangesPanel
          formulaType={formulaType}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
          onRowChange={onRowChange}
          rows={rows as RangeFormulaRow[]}
          specType={specType}
        />
      ) : null}
    </div>
  );
};

interface SpecCardTierModeControlProps {
  onChange: (specType: SpecType, tierMode: TierMode) => void;
  specType: SpecType;
  tierMode: TierMode;
}

// Threshold/Range switch shown above the tiered configuration — the single decision point that
// determines how many inputs the user has to fill in below. A true single-select `RadioButtonGroup`
// (`variant="button-group"`), matching Formula Type/Decimal Precision alongside it in height.
//
// `onChange` is wired on the *group*, not each `Radio` — `RadioGroup` clones every `Radio` child
// with its own `onChange` prop (to inject the shared `name`), which silently overwrites (not
// merges with) whatever `onChange` an individual `Radio` was given. Reading the selected value
// back off `event.target.value` (always a string; `TierMode`'s members are themselves string
// enum values) is therefore the only `onChange` that actually fires.
const SpecCardTierModeControl: React.FC<SpecCardTierModeControlProps> = ({ onChange, specType, tierMode }) => (
  <div className="qfw-tier-mode" style={{ flex: '0 0 auto', ...colFlex('var(--slds-g-spacing-1)') }}>
    <span className="slds-form-element__label" style={{ marginBottom: 0 }}>
      Tier Mode
      <FieldHelpTooltip label={FIELD_HELP.tierMode} />
    </span>
    <RadioButtonGroup
      name={`tier-mode-${specType}`}
      onChange={event => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment -- see ProductPriceBasisSection.tsx
        onChange(specType, event.target.value as TierMode);
      }}
    >
      {TIER_MODES.map(mode => (
        <Radio
          checked={tierMode === mode.value}
          key={mode.value}
          labels={{ label: mode.label }}
          value={mode.value}
          variant="button-group"
        />
      ))}
    </RadioButtonGroup>
  </div>
);

interface SpecCardSingleRowConfigProps {
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof SingleValueFormulaRow,
    value: number | string,
  ) => void;
  singleRowConfig: SingleRowConfig;
  specType: SpecType;
  state: SpecState;
}

// Extracted so the non-tiered (single-value) "Contracted Value" panel doesn't add to the parent
// component's complexity. Renders the same panel as the tiered Contracted Value box (see
// SpecCardContractedValuePanel) since both represent the same concept — just backed by a
// different row field per formula type (see SINGLE_ROW_CONFIG_BY_FORMULA_TYPE).
const SpecCardSingleRowConfig: React.FC<SpecCardSingleRowConfigProps> = ({
  onRowChange,
  singleRowConfig,
  specType,
  state,
}) => {
  const row = state.rows[0] as SingleValueFormulaRow | undefined;
  return (
    <SpecSectionPanel description={singleRowConfig.hint} iconName="number_input" title={singleRowConfig.label}>
      <FormulaField
        fallback={singleRowConfig.fallback}
        label={singleRowConfig.label}
        onChange={value => {
          onRowChange(specType, 0, singleRowConfig.field, value);
        }}
        value={row?.[singleRowConfig.field] ?? singleRowConfig.fallback}
      />
    </SpecSectionPanel>
  );
};

interface ContractedValuePanelProps {
  formulaType: QualityFormulaType;
  onContractedSpecChange: (specType: SpecType, value: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof SingleValueFormulaRow,
    value: number | string,
  ) => void;
  specType: SpecType;
  state: SpecState;
}

// Contracted Value (or the single-row equivalent) on its own, so QualitySpecCard can place it independently.
// Re-derives `hasTieredRows` from `formulaType` itself (rather than taking it as a prop) so
// TypeScript's aliased-condition narrowing still applies to the
// `SINGLE_ROW_CONFIG_BY_FORMULA_TYPE` lookup below — that narrowing doesn't carry over a boolean
// computed in a different component.
const ContractedValuePanel: React.FC<ContractedValuePanelProps> = ({
  formulaType,
  onContractedSpecChange,
  onRowChange,
  specType,
  state,
}) => {
  const hasTieredRows = isMultiRowFormulaType(formulaType);

  // QuantityAdjustedByPercent (e.g. Nitrogen) has no contracted baseline at all — billable
  // quantity is purely the actual % of shipped quantity — so there's nothing to configure here.
  if (formulaType === QualityFormulaType.QuantityAdjustedByPercent) {
    return (
      <SpecSectionPanel
        description="This formula has no contracted baseline — billable quantity is always the actual % of shipped quantity."
        iconName="number_input"
        title="No Contracted Value"
      >
        <p className="slds-text-body_small slds-text-color_weak">Not applicable for this formula type.</p>
      </SpecSectionPanel>
    );
  }

  if (!hasTieredRows) {
    return (
      <SpecCardSingleRowConfig
        onRowChange={onRowChange}
        singleRowConfig={SINGLE_ROW_CONFIG_BY_FORMULA_TYPE[formulaType]}
        specType={specType}
        state={state}
      />
    );
  }

  return (
    <SpecSectionPanel
      description="The value every tier is measured against."
      iconName="number_input"
      title="Contracted Value"
      tooltip={FIELD_HELP.contractedValue}
    >
      <FormulaField
        error={validateContractedSpec(state.contractedSpec, specType, formulaType)}
        fallback={0}
        label="Contracted Value"
        onChange={value => {
          onContractedSpecChange(specType, value);
        }}
        value={state.contractedSpec}
      />
    </SpecSectionPanel>
  );
};

interface ConfigurationPanelProps {
  allowedOptions: Array<{ label: string; value: QualityFormulaType }>;
  hasTieredRows: boolean;
  onFormulaTypeChange: (specType: SpecType, formulaType: QualityFormulaType) => void;
  onPrecisionChange: (specType: SpecType, value: number) => void;
  onPriceBasisChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  onTierModeChange: (specType: SpecType, tierMode: TierMode) => void;
  // Locks the Price Basis selector to its current value — the Template Editor always keeps it at
  // Contract Price; choosing Deemed Price or Price Components is a Contract Entry-only action.
  priceBasisLocked?: boolean;
  showsContractPriceInfo: boolean;
  showsDeemedPriceField: boolean;
  showsPriceBasis: boolean;
  specType: SpecType;
  state: SpecState;
}

// Formula Type/Tier Mode/Decimal Precision/Price Basis — extracted so QualitySpecCard can place
// it either beside Contracted Value (Price Components, see showsPriceComponentsSplitLayout) or
// above it full-width (every other Price Basis, per the Designer reference) without duplicating
// this markup.
const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  allowedOptions,
  hasTieredRows,
  onFormulaTypeChange,
  onPrecisionChange,
  onPriceBasisChange,
  onTierModeChange,
  priceBasisLocked,
  showsContractPriceInfo,
  showsDeemedPriceField,
  showsPriceBasis,
  specType,
  state,
}) => {
  const selectedFormulaOption = allowedOptions.find(option => option.value === state.formulaType);

  // Price Basis sits in Row 1 (next to Precision) only when it has no inline follow-up of its
  // own to share a row with — for Price Components (whose panel is below), or when creating a
  // template without the sample Contract Price callout. Otherwise Price Basis stays beside its
  // inline follow-up (the contract price note or Deemed Price field) in Row 2.
  const showsPriceBasisInRow1 = showsPriceBasis && !showsContractPriceInfo && !showsDeemedPriceField;

  return (
    <SpecSectionPanel
      description="Choose the formula and settings for this quality adjustment."
      iconName="settings"
      title="Configuration"
    >
      <div style={colFlex('var(--slds-g-spacing-3)')}>
        {/* Row 1 — Formula Type, Tier Mode (if tiered), Decimal Precision, plus Price Basis only
            when `showsPriceBasisInRow1` (see its own doc comment above). Tier Mode/Decimal
            Precision/Price Basis are all fixed-basis, non-growing (`flex: '0 1 ...'` / a modest
            `'1 1 200px'` for Price Basis's own Combobox) — unlike Formula Type, none needs more
            room than its own content, so they sit snugly next to each other instead of a
            `flex-grow` spreading them out toward the row's far edge. */}
        <div style={rowFlex('var(--slds-g-spacing-3)')}>
          <div style={{ flex: '1 1 240px' }}>
            <FieldLabelRow label="Formula Type" tooltip={FIELD_HELP.formulaType} />
            <Combobox
              assistiveText={{ label: 'Formula Type' }}
              events={{
                onSelect: (_event, data) => {
                  const selectedId = data.selection[0]?.id;
                  // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
                  if (selectedId) onFormulaTypeChange(specType, selectedId as QualityFormulaType);
                },
              }}
              id={`formula-type-${specType}`}
              labels={{ placeholder: 'Select Formula Type' }}
              options={allowedOptions.map(option => ({ id: option.value, label: option.label }))}
              predefinedOptionsOnly
              selection={
                selectedFormulaOption ? [{ id: selectedFormulaOption.value, label: selectedFormulaOption.label }] : []
              }
              variant="readonly"
            />
          </div>
          {hasTieredRows ? (
            <SpecCardTierModeControl onChange={onTierModeChange} specType={specType} tierMode={state.tierMode} />
          ) : null}
          <div style={{ flex: '0 1 140px' }}>
            <FormulaField
              fallback={0}
              label="Precision"
              onChange={value => {
                onPrecisionChange(specType, Math.max(0, Math.min(10, value)));
              }}
              tooltip={FIELD_HELP.precision}
              value={state.precision}
            />
          </div>
          {showsPriceBasisInRow1 ? (
            <div style={{ flex: '1 1 200px' }}>
              <ProductPriceBasisField
                disabled={priceBasisLocked}
                onChange={onPriceBasisChange}
                priceBasis={state.priceBasis}
                specType={specType}
              />
            </div>
          ) : null}
        </div>
        {/* Row 2 — Price Basis beside the Contract Price note or Deemed Price field, when present. */}
        {showsPriceBasis && !showsPriceBasisInRow1 ? (
          <div style={rowFlex('var(--slds-g-spacing-3)')}>
            <div style={{ flex: '1 1 220px' }}>
              <ProductPriceBasisField
                disabled={priceBasisLocked}
                onChange={onPriceBasisChange}
                priceBasis={state.priceBasis}
                specType={specType}
              />
            </div>
            {showsContractPriceInfo ? (
              <div style={{ alignSelf: 'flex-end', flex: '2 1 280px' }}>
                <ContractPriceBasisInfo />
              </div>
            ) : null}
            {showsDeemedPriceField ? (
              <div style={{ flex: '1 1 180px' }}>
                <DeemedPriceField onChange={onPriceBasisChange} priceBasis={state.priceBasis} specType={specType} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </SpecSectionPanel>
  );
};

interface QualitySpecCardProps {
  /** Templates select their one spec from the form above instead of removing it from the card. */
  allowRemoveSpec?: boolean;
  /** Defaults to `true`; set to `false` by the Template Editor, where there's only ever one card
   * and it must stay expanded while editing — see `SpecCardHeader`'s own doc comment. */
  collapsible?: boolean;
  /** Omit the sample-contract price callout while creating a template. */
  hideContractPriceInfo?: boolean;
  hint: string;
  // Locks the Price Basis selector to its current value — set by the Template Editor, where Price
  // Basis always stays Contract Price; choosing Deemed Price or Price Components is a Contract
  // Entry-only action.
  priceBasisLocked?: boolean;
  // Omitted entirely in the Template Editor — loading a template while creating/editing another
  // template doesn't make sense (that's a Contract Entry action). Rendering the control itself is
  // left to the caller (e.g. QualityFormulasPage), not imported here, so this quality-formulas
  // feature component never has to depend on the Templates feature.
  renderLoadTemplateControl?: RenderLoadTemplateControl;
  specLabel: string;
  specType: SpecType;
  /** `'card'` (default) is the routed Template Editor's card. `'focused'` is the single-editor
   * workbench used by Contract Entry and the inline Template Manager — never collapsible, with
   * a richer header (tier mode in the title,
   * active Formula Type in the subtitle, a "Configured" status badge) since it's the only card on
   * screen rather than one of several stacked ones. */
  variant?: 'card' | 'focused';
}

// Reads this card's own slice of `QualityFormulaStoreProvider` directly (rather than taking
// `SpecState` + a dozen handlers as props) so neither page that renders a grid of these
// (`QualityFormulasPage`, `TemplateEditorPage`) has to thread every Step 1 handler through just to
// mount a card — only genuinely page-supplied concerns (labels, icons, the Load Template slot)
// remain props.
//
// Wrapped in `memo` so a page re-render (e.g. `QualityFormulasPage` re-rendering for an unrelated
// reason) doesn't force React to reconcile every other spec's card too — since this card's own
// Zustand selector above is what actually drives its content, and its props are otherwise stable,
// `memo` lets an edit to one spec's fields skip right past every unrelated card.
const QualitySpecCardComponent: React.FC<QualitySpecCardProps> = ({
  allowRemoveSpec = true,
  collapsible = true,
  hideContractPriceInfo = false,
  hint,
  priceBasisLocked,
  renderLoadTemplateControl,
  specLabel,
  specType,
  variant = 'card',
}) => {
  const state = useQualityFormulaStore(store => store.specStates[specType]);
  const {
    applyTemplateToSpec,
    onAddRow,
    onContractedSpecChange,
    onFormulaTypeChange,
    onPrecisionChange,
    onPriceBasisChange,
    onRemoveRow,
    onRemoveSpec,
    onRowChange,
    onThresholdSideToggle,
    onTierModeChange,
    onToggleCollapse,
  } = useQualityFormulaStore(
    useShallow(store => ({
      applyTemplateToSpec: store.applyTemplateToSpec,
      onAddRow: store.handleAddRow,
      onContractedSpecChange: store.handleContractedSpecChange,
      onFormulaTypeChange: store.handleFormulaTypeChange,
      onPrecisionChange: store.handlePrecisionChange,
      onPriceBasisChange: store.handlePriceBasisChange,
      onRemoveRow: store.handleRemoveRow,
      onRemoveSpec: store.removeSpec,
      onRowChange: store.handleRowChange,
      onThresholdSideToggle: store.handleThresholdSideToggle,
      onTierModeChange: store.handleTierModeChange,
      onToggleCollapse: store.handleToggleCollapse,
    })),
  );
  const onApplyTemplate = (config: TemplateSpecConfig): void => {
    applyTemplateToSpec(specType, config);
  };
  const effectCopy = getSpecEffectCopy(specType);
  const allowedOptions = FORMULA_TYPES.filter(option => getAllowedFormulaTypes(specType).includes(option.value)).map(
    option => ({ label: option.label, value: option.value }),
  );
  const { formulaType } = state;
  const hasTieredRows = isMultiRowFormulaType(formulaType);
  const isThreshold = state.tierMode === TierMode.Threshold;
  // Product Price Basis only applies to "% of Price" (PERCENTAGE) formulas — see business rule
  // in ProductPriceBasisSection's own doc comment.
  const showsPriceBasis = formulaType === QualityFormulaType.Percentage;
  const showsContractPriceInfo =
    showsPriceBasis && state.priceBasis.type === ProductPriceBasisType.ContractPrice && !hideContractPriceInfo;
  const showsDeemedPriceField = showsPriceBasis && state.priceBasis.type === ProductPriceBasisType.DeemedPrice;
  const showsPriceComponentsPanel = showsPriceBasis && state.priceBasis.type === ProductPriceBasisType.PriceComponents;
  // Price Components is the one Price Basis whose Designer reference splits the card into two
  // columns (Configuration+Price Components left, Contracted Value/Applies to/Adjustment Behavior
  // right) — every other combination (including Price Components + Range, with no reference of
  // its own) stacks every panel full-width instead, per the Contract Price/Deemed Price reference.
  const showsPriceComponentsSplitLayout = showsPriceComponentsPanel && hasTieredRows && isThreshold;
  const activeFormulaLabel = getActiveFormulaLabel(state.formulaType, specLabel);
  const tierCountLabel = getTierCountLabel(state.tierMode, state.rows.length);
  const priceComponentBadgeLabels = showsPriceComponentsPanel
    ? CONTRACT_PRICE_COMPONENTS.filter(component => state.priceBasis.selectedComponentKeys.includes(component.key)).map(
        component => `${component.label}: ${formatCurrency(component.value * component.sign)}`,
      )
    : undefined;
  const deemedPriceBadgeLabel = showsDeemedPriceField
    ? `Deemed Price: ${formatCurrency(state.priceBasis.deemedPrice)}`
    : undefined;
  const isFocused = variant === 'focused';
  const { isCollapsedSummary, isCollapsible } = getCollapseBehavior(variant, collapsible, state.collapsed);
  const isExpanded = !isCollapsedSummary;
  const { isConfigured, tierModeLabel } = getFocusedHeaderDetails(isFocused, hasTieredRows, specType, state);

  const configurationPanel = (
    <ConfigurationPanel
      allowedOptions={allowedOptions}
      hasTieredRows={hasTieredRows}
      onFormulaTypeChange={onFormulaTypeChange}
      onPrecisionChange={onPrecisionChange}
      onPriceBasisChange={onPriceBasisChange}
      onTierModeChange={onTierModeChange}
      priceBasisLocked={priceBasisLocked}
      showsContractPriceInfo={showsContractPriceInfo}
      showsDeemedPriceField={showsDeemedPriceField}
      showsPriceBasis={showsPriceBasis}
      specType={specType}
      state={state}
    />
  );
  const contractedValuePanel = (
    <ContractedValuePanel
      formulaType={formulaType}
      onContractedSpecChange={onContractedSpecChange}
      onRowChange={onRowChange}
      specType={specType}
      state={state}
    />
  );

  return (
    <Card className="qfw-spec-card" hasNoHeader>
      {/* The header is rendered as plain body content (not via `Card`'s own `header` prop) — that
          prop clones `figure`/`body`/`verticalCenter`/`canTruncate` onto whatever element it's
          given expecting a `MediaObject`-shaped target; handed this custom two-column header
          instead, those props land as invalid attributes on a plain `<div>` (the
          "React does not recognize the `verticalCenter`/`canTruncate` prop" warning) and — more
          visibly — the div Card wraps it in *doubles up* with this div's own, stacking the real
          `.slds-card__header` padding on top of itself and throwing the header noticeably further
          from the card's top edge than every other panel's (Playground/SpecSectionPanel) own
          single-layer header. Rendering it as the body's own first child instead gives it the
          exact same padding every other section gets, with no extra wrapper. */}
      <div className="slds-p-bottom_small">
        <SpecCardHeader
          activeFormulaLabel={activeFormulaLabel}
          deemedPriceBadgeLabel={deemedPriceBadgeLabel}
          effectCopy={effectCopy}
          hasTieredRows={hasTieredRows}
          hint={hint}
          isCollapsedSummary={isCollapsedSummary}
          isCollapsible={isCollapsible}
          isConfigured={isConfigured}
          isFocused={isFocused}
          onApplyTemplate={onApplyTemplate}
          onRemoveSpec={
            isFocused && allowRemoveSpec
              ? () => {
                  onRemoveSpec(specType);
                }
              : undefined
          }
          onToggleCollapse={onToggleCollapse}
          priceComponentBadgeLabels={priceComponentBadgeLabels}
          renderLoadTemplateControl={renderLoadTemplateControl}
          specLabel={specLabel}
          specType={specType}
          tierCountLabel={tierCountLabel}
          tierModeLabel={tierModeLabel}
        />
      </div>
      {isExpanded ? (
        <>
          {/* Separates the header (icon/title/Load Template/Configured badge) from the sections
              below, matching the Ash/Sulfur/BTU references' own full-width rule there — Card's
              header and body are otherwise only distinguished by padding, with no visible seam. */}
          <hr className="qfw-divider slds-p-bottom_small" />
          {showsPriceComponentsSplitLayout ? (
            // `alignItems: 'stretch'` (not `flex-start`) so Price Components starts at the same
            // height as the right column, growing past it instead of clipping if it needs more
            // room — see SpecSectionPanel/PriceComponentsTable's own `fillHeight` doc comments.
            <div style={{ ...rowFlex('var(--slds-g-spacing-2)'), alignItems: 'stretch' }}>
              <div style={{ ...colFlex(), flex: '2 1 340px', minWidth: 0 }}>
                {/* Keep Configuration at its natural height so only Price Components can grow
                    to fill the rest of the split column. */}
                <div style={{ flex: '0 0 auto' }}>{configurationPanel}</div>
                <div style={{ flex: 1 }}>
                  <ProductPriceBasisDetails
                    fillHeight
                    onChange={onPriceBasisChange}
                    priceBasis={state.priceBasis}
                    specType={specType}
                  />
                </div>
              </div>
              <div className="qfw-price-components-side" style={{ ...colFlex(), flex: '1 1 300px', minWidth: 0 }}>
                {contractedValuePanel}
                <AdjustmentBehaviorPanel
                  contractedSpec={state.contractedSpec}
                  formulaType={QualityFormulaType.Percentage}
                  onRowChange={onRowChange}
                  onThresholdSideToggle={onThresholdSideToggle}
                  rows={state.rows as ThresholdFormulaRow[]}
                  specType={specType}
                />
              </div>
            </div>
          ) : (
            <SpecCardStackedLayout
              configurationPanel={configurationPanel}
              contractedValuePanel={contractedValuePanel}
              formulaType={formulaType}
              isThreshold={isThreshold}
              onAddRow={onAddRow}
              onPriceBasisChange={onPriceBasisChange}
              onRemoveRow={onRemoveRow}
              onRowChange={onRowChange}
              onThresholdSideToggle={onThresholdSideToggle}
              priceBasis={state.priceBasis}
              rows={state.rows as Array<RangeFormulaRow | ThresholdFormulaRow>}
              showsPriceComponentsPanel={showsPriceComponentsPanel}
              specType={specType}
              state={state}
            />
          )}
        </>
      ) : null}
    </Card>
  );
};

export const QualitySpecCard = memo(QualitySpecCardComponent);
